import React, { FC, Fragment, useCallback, useContext, useMemo, useRef } from 'react'
import HomePageTransport from '../../transports/home-page-transport'
import NewsLetterSection from '../common/news-letter-section'
import CommonHeroSection from '../common/hero-section'
import { HOME_BACKGROUND_IMAGES } from '../common/hero-section/constants'
import TrustBar from '../common/trust-bar-section'
import MegaBar from '../common/megabar'
import PropertyTypesSection from './property-types'
import { Container, InnerContainer } from './styles'
import OfferCardsSection from './offer-cards-section'
import UserDetailsModel from '../../models/user/user-details-model'
import { ScreenContext } from 'react-components'
import { useScrollToSearch } from '../../hooks/scroll-to-search'
import HeaderSection from '../common/header-section'
import RegionCountSection from './region-count-section'

import HolidayIdeaSection from './holiday-ideas'
import PopularSearchesSection from './popular-searches'
import { getPopularSearchesByLocale } from './popular-searches/popular-searches-data'
import { useTranslation, useLocale } from '../../hooks/locale'
import { BreadcrumbList } from '../../models/common/breadcrumb-model'
import { Offer } from '../../models/common/offers-model'
import { SuitablesModel } from '../../models/common/suitables-model'
import { useDateFormatter } from '../../hooks/date'
import ReviewSection from './review-section'
import OtherPopularRegionSection from './other-popular-region-section'
import FooterSection from '../common/footer'
import FaqSection from './faq-section'
import BannerAdSection from './banner-ad-slot/index'
import SeoSection from './seo-section'
import ContinueSearchWidget from './continue-search-widget'
import TopOffersSection from './top-offers-section'
import TopRecommendationsSection from './top-recommendations-section'
import { AB_KEY, AB_VARIANT_VERSION, LOCALES } from '../../common/constants'
import { useABVariant } from '../../utils/hooks'

type Props = {
    isLoggedIn: boolean
    userDetails: UserDetailsModel | null
    url: string
    sitePathname: string
    homeDetails: HomePageTransport | null
}

const HomePage: FC<Props> = ({ userDetails, isLoggedIn, url, sitePathname, homeDetails }) => {
    const { t } = useTranslation()
    const { format } = useDateFormatter()
    const { locale } = useLocale()
    const breadcrumbModel = useMemo(() => new BreadcrumbList(t), [t])
    const offerModel = useMemo(() => new Offer(t, format), [t, format])
    const suitablesModel = useMemo(() => new SuitablesModel(t), [t])
    const { isMobile } = useContext(ScreenContext)

    const { isProvidedVariantActive: isPopularSearchesActive } = useABVariant(
        AB_KEY.POPULAR_SEARCHES,
        AB_VARIANT_VERSION.ONE,
    )

    const searchbarRef = useRef<HTMLDivElement>(null)
    const showHeaderSearch = useScrollToSearch(searchbarRef, isMobile)

    const scrollToSearchbar = useCallback(() => {
        if (searchbarRef.current) {
            searchbarRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }, [])

    if (!homeDetails) {
        return null
    }

    const {
        staticData,
        trustBar,
        propertyTypeSection,
        holidayIdeas,
        regionMap,
        topRatedObjects: topRatedObjectsSection,
        otherPopularRegions: otherPopularRegionsSection,
        topOffers,
        recommendedObjects,
    } = homeDetails

    const {
        navigationSection,
        megabar,
        heroSection,
        offerCardSection,
        newsLetterSection,
        footerSection,
        banners,
        faqSection,
        seoSection,
    } = staticData

    const headerSection = { navigationSectionResponse: navigationSection }

    return (
        <Fragment>
            {navigationSection ? (
                <HeaderSection
                    firstName={userDetails?.firstName}
                    isLoggedIn={isLoggedIn}
                    url={url}
                    headerSection={headerSection}
                    sitePathname={sitePathname}
                    showVacationIdeasNavButton
                    megabar={megabar}
                    onSearchIconClick={showHeaderSearch ? scrollToSearchbar : undefined}
                />
            ) : null}

            {!!megabar && <MegaBar megabar={megabar} />}

            <Container>
                {!!heroSection && (
                    <CommonHeroSection
                        title={heroSection.title}
                        subtitle={heroSection.subtitle}
                        backgroundImages={HOME_BACKGROUND_IMAGES}
                        ref={searchbarRef}
                        logo={headerSection?.navigationSectionResponse?.navigation?.logo}
                        textColor="white"
                    />
                )}
                {!!trustBar && <TrustBar trustBar={trustBar} />}
                <InnerContainer>
                    {locale === LOCALES.DE_DE && isPopularSearchesActive && (
                        <PopularSearchesSection items={getPopularSearchesByLocale(locale)} />
                    )}

                    <ContinueSearchWidget
                        url={url}
                        breadcrumbModel={breadcrumbModel}
                        offerModel={offerModel}
                        suitablesModel={suitablesModel}
                    />
                    {!!offerCardSection && <OfferCardsSection offerCardSection={offerCardSection} />}
                    {!!propertyTypeSection?.items?.length && (
                        <PropertyTypesSection
                            section={propertyTypeSection}
                            totalOnlineListingsCount={trustBar?.totalOnlineListingsCount}
                        />
                    )}
                    {!!holidayIdeas?.items && holidayIdeas.items.length > 0 && (
                        <HolidayIdeaSection items={holidayIdeas?.items} />
                    )}
                    {!!topOffers && topOffers?.objects?.length > 0 && (
                        <TopOffersSection
                            listingSection={topOffers}
                            url={url}
                            defaultHeading={t('pages.home.topOffers.heading')}
                            defaultSubheading={t('pages.home.topOffers.subheading')}
                            breadcrumbModel={breadcrumbModel}
                            offerModel={offerModel}
                            suitablesModel={suitablesModel}
                        />
                    )}
                    {!!recommendedObjects && recommendedObjects?.objects?.length > 0 && (
                        <TopRecommendationsSection
                            listingSection={recommendedObjects}
                            url={url}
                            defaultHeading={t('pages.home.topRecommendations.heading')}
                            breadcrumbModel={breadcrumbModel}
                            offerModel={offerModel}
                            suitablesModel={suitablesModel}
                        />
                    )}
                    {!!regionMap && <RegionCountSection regionsMap={regionMap} />}
                    {!!topRatedObjectsSection && topRatedObjectsSection?.objects?.length > 0 && (
                        <ReviewSection
                            topRatedObjectsSection={topRatedObjectsSection}
                            breadcrumbList={breadcrumbModel}
                            url={url}
                            defaultHeading={t('pages.home.topRated.heading')}
                        />
                    )}
                    {!!otherPopularRegionsSection && otherPopularRegionsSection?.otherRegions?.length > 0 && (
                        <OtherPopularRegionSection regions={otherPopularRegionsSection.otherRegions} />
                    )}
                    {!!seoSection && <SeoSection seoQuestions={seoSection} />}
                    {!!faqSection && <FaqSection faqs={faqSection} />}
                    {!!banners?.slot1 && <BannerAdSection banner={banners.slot1} slotId="gtm-ad-slot-1" />}
                    {!!banners?.slot2 && <BannerAdSection banner={banners.slot2} slotId="gtm-ad-slot-2" />}
                    {!!banners?.slot3 && <BannerAdSection banner={banners.slot3} slotId="gtm-ad-slot-3" />}
                    {!!newsLetterSection && <NewsLetterSection newsletter={newsLetterSection} url={url} />}
                </InnerContainer>
                {!!footerSection && (
                    <FooterSection
                        footerSection={footerSection}
                        footerLogo={headerSection.navigationSectionResponse.navigation.logo}
                        socialMediaLinks={headerSection.navigationSectionResponse.socialMediaLinks}
                    />
                )}
            </Container>
        </Fragment>
    )
}

export default HomePage
